import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Button, Field, Input } from './ui';

const Login = () => {
  const { login, error, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleLogin = (event) => {
    event.preventDefault();
    login(email.trim(), password).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-ink-50 flex">
      {/* Left — institutional brand panel (calm, no gradient) */}
      <div className="hidden lg:flex lg:flex-1 bg-ink-900 relative overflow-hidden">
        <div className="flex flex-col justify-between p-12 text-white relative z-10 w-full">
          <div className="flex items-center gap-3">
            <img src="/logo-white.svg" alt="Tabadulat" className="h-8 w-auto" />
            <p className="text-white/40 text-xs leading-tight">Partner Portal</p>
          </div>

          <div className="max-w-md">
            <h1 className="text-3xl font-semibold tracking-tight leading-tight">
              The control center for your financial institution.
            </h1>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">
              Manage your customers, portfolios, team access, API clients, and audit
              history — all from a single, secure workspace.
            </p>
            <div className="mt-8 space-y-3">
              {[
                'Role-based access for owners, admins, and members',
                'API client credentials for secure server-to-server access',
                'Immutable audit trail for every configuration change',
              ].map((line) => (
                <div key={line} className="flex items-start gap-2.5 text-sm text-white/70">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-success-500 shrink-0" />
                  <span>{line}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} Tabadulat. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right — login form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/logo.png" alt="Tabadulat" className="h-8 w-auto" />
            <p className="text-ink-400 text-xs leading-tight">Partner Portal</p>
          </div>

          <h2 className="text-xl font-semibold tracking-tight text-ink-900">
            Sign in to your workspace
          </h2>
          <p className="mt-1.5 text-sm text-ink-500">
            Use your organization administrator credentials to continue.
          </p>

          {error && (
            <div className="mt-6 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-800">
              {error}
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={handleLogin}>
            <Field label="Email address" required>
              <Input
                type="email"
                name="email"
                aria-label="Email address"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={loading}
                required
              />
            </Field>

            <Field label="Password" required>
              <Input
                type="password"
                name="password"
                aria-label="Password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={loading}
                minLength={8}
                required
              />
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="md"
              className="w-full"
              iconRight={ArrowRight}
              loading={loading}
              disabled={!email.trim() || password.length < 8}
            >
              Sign in
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-ink-200">
            <p className="text-xs text-ink-500 leading-relaxed">
              By continuing you agree to the Tabadulat Partner Agreement and acknowledge
              our Privacy Policy. This service is intended for authorized personnel of
              partner financial institutions.
            </p>
          </div>

          <div className="mt-6 flex items-center gap-1.5 text-xs text-ink-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Secured · SOC 2 Type II · ISO 27001</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
